/* eslint-disable @typescript-eslint/no-unused-vars */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetch3, fetch5, fetch6, fetch7 } from "../commonApis/commonApis";
import { addProfileData } from "../features/auth/authSlice";
import { useDispatch, useSelector } from "react-redux";
import { useEffect } from "react";
import { setClientPermissions } from "../features/auth/authSlice";

const url = process.env.REACT_APP_BASE_URL;
interface ApiResponse {
  success: boolean;
  data: any;
  message?: string;
  statusCode?: number;
  statusMessage?: string;
  error?: any;
}

export const useProfileData = () => {
  const token = localStorage.getItem("token"); // or sessionStorage if that’s what you use
  const dispatch = useDispatch();
  return useQuery({
    queryKey: ["getMe"],
    queryFn: async () => {
      const res = await fetch3(`${url}/api/auth/getMe?type=outlet`, "get");

      if (!res || !res.data) {
        return null; // 👈 always return a predictable type
      }

      dispatch(addProfileData(res.data));
      return res.data; // full user object
    },
    staleTime: 1000 * 60 * 5,
    // enabled: !!token, // 👈 don’t even run if logged out
  });
};

export const useClientProfileData = () => {
  return useQuery({
    queryKey: ["getClientData"],
    queryFn: async () => {
      const res = await fetch3(
        `${url}/api/lm-client/profile?type=outlet`,
        "get",
      );

      if (!res || !res.data) {
        return res.message;
      }

      return res.data; // 👈 This returns your full user object
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useClientConfigurationData = () => {
  return useQuery({
    queryKey: ["getClientConfigurationData"],
    queryFn: async () => {
      const res = await fetch3(`${url}/api/client/configuration`, "get");

      if (!res || !res.data) {
        return res.message;
      }

      return res.data; // 👈 This returns your full user object
    },
    staleTime: 1000 * 60 * 5,
  });
};

interface UpdateProfilePayload {
  companyName: string;
  contactPerson: string;
  companyCrNo: string;
  companyVatNo: string;
  systemMail: string;
  country: string;
  email: string;
  phoneNumber: string;
  state: string;
  city: string;
  zipCode: string;
  clientAddress: string;
  lmAwbNumber: string;
  shortAddress: string;
  tollFreeNo: string;
  crDoc: File;
  vatDoc: File;
}
export const useUpdateProfile = () => {
  return useMutation({
    mutationFn: async (payload: UpdateProfilePayload) => {
      const res: ApiResponse = await fetch7(
        `${url}/api/outlet/update/profile?type=outlet`,
        "PATCH",
        payload,
      );

      if (!res.success) {
        throw new Error(res.message || "Failed to update user");
      }

      return res.data;
    },
    onSuccess: (data: any) => {
      console.log("✅ User updated successfully:", data);
    },
    onError: (error: any) => {
      console.error("❌ User update request failed:", error.message);
    },
  });
};

export const useUpdatePasswordProfile = () => {
  return useMutation({
    mutationFn: async (payload: UpdateProfilePayload) => {
      const res: ApiResponse = await fetch7(
        `${url}/api/lm-client/update/password?type=outlet`,
        "PATCH",
        payload,
      );

      if (!res.success) {
        throw new Error(res.message || "Failed to update password");
      }

      return res.data;
    },
    onSuccess: (data: any) => {
      console.log("✅ Password updated successfully:", data);
    },
    onError: (error: any) => {
      console.error("❌ Password update request failed:", error.message);
    },
  });
};

export const useUpdateProfilePicMutation = () => {
  return useMutation({
    mutationFn: async (payload: any) => {
      const res: ApiResponse = await fetch6(
        `${url}/api/outlets/update/logo?type=outlet`,
        payload,
      );

      // Manually throw error if success is false
      if (!res.success) {
        throw res.message || "Something went wrong";
      }

      return res.data;
    },
    onSuccess: (data: any) => {
      console.log("✅ Driver Assign successfully:", data);
    },
    onError: (error: any) => {
      console.error("❌ Driver Assign request failed:", error.message);
    },
  });
};

export const useUpdateConfigurationMutation = () => {
  return useMutation({
    mutationFn: async (payload: any) => {
      const res: ApiResponse = await fetch5(
        `${url}/api/client/update/configuration`,
        "PUT",
        payload,
      );

      // Manually throw error if success is false
      if (!res.success) {
        throw res.message || "Something went wrong";
      }

      return res.data;
    },
    onSuccess: (data: any) => {
      console.log("✅ Driver Assign successfully:", data);
    },
    onError: (error: any) => {
      console.error("❌ Driver Assign request failed:", error.message);
    },
  });
};

interface ApiPermission {
  module: string;
  permissions: string[];
}

export interface ClientPermission {
  module: string;
  operations: string[];
}
const normalise = (api: ApiPermission[]): ClientPermission[] =>
  api.map((item) => ({
    module: item.module,
    operations: item.permissions,
  }));

export const useClientPermissions = () => {
  const dispatch = useDispatch();
  const profile = useSelector((state: any) => state.auth.profile);

  const query = useQuery({
    queryKey: ["clientPermissions", profile?.id],
    enabled: !!profile?.id, // 🔥 now TRUE
    queryFn: async () => {
      const res = await fetch3(
        `${url}/api/access-center/${profile.id}/accesses?type=outlet`,
        
        "get"
      );

      if (!res?.success) throw new Error("Failed");

      return res.data;
    },
    select: normalise,
  });

  useEffect(() => {
    if (query.data) {
      dispatch(setClientPermissions(query.data));
    }
  }, [query.data, dispatch]);

  return query;
};



// ── GET Outlet Profile ──
export const useGetOutletProfile = () => {
  return useQuery({
    queryKey: ["getOutletProfile"],
    queryFn: async () => {
      const res = await fetch3(
        `${url}/api/outlets/profile?type=outlet`,
        "get"
      );

      if (!res || !res.data) {
        return null;
      }

      return res.data;
    },
    staleTime: 1000 * 60 * 5,
  });
};

// ── UPDATE Outlet Profile ──

export const useUpdateOutletProfile = () => {
  return useMutation({
    mutationFn: async (payload: any) => {
      const res: ApiResponse = await fetch5(  
        `${url}/api/outlets/profile?type=outlet`,
        "PATCH",                              
        payload
      );

      if (!res.success) {
        throw new Error(res.message || "Failed to update outlet profile");
      }

      return res.data;
    },
    onSuccess: (data: any) => {
      console.log("✅ Outlet profile updated successfully:", data);
    },
    onError: (error: any) => {
      console.error("❌ Outlet profile update failed:", error.message);
    },
  });
};